/**
 * Montage de la nouvelle interface de réservation (docs/ux-v2/SPEC.md § 6.2), chargé par
 * main.tsx (import dynamique) SEULEMENT si isUxV2() : flag éteint, ni ce fichier ni rien de la v2
 * n'est téléchargé (morceau dist/v2/, CSS compris : styles/inject.ts). Les règles de
 * ux-v2.css sont toutes préfixées par #mindbody-app[data-ux='v2'].mmv2.
 *
 * Mêmes providers que la v1 (React Query partagé, utilisateur, HashRouter), thème Chakra v2,
 * variables --mmv2-* posées sur #mindbody-app, tag Sentry ux=v2 pour trier les erreurs.
 */
import { ChakraProvider } from '@chakra-ui/react';
import { UserProvider } from '@providers/useUserContext.tsx';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import type { Root } from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
import { Fonts } from 'theme/fonts/index.tsx';
// Socle CSS posé avant les feuilles des écrans (importées par AppV2) : styles/base.ts.
import './styles/base';
import { AfterFirstCommit } from './AfterFirstCommit';
import { AppV2 } from './AppV2';
import { runPendingHandoff } from './lib/handoff';
import { captureServerSkeleton, serverFontAliases, withServerFontAlias } from './lib/planning/skeleton';
import { V2_TOAST_DEFAULTS } from './lib/toastDefaults';
import { createThemeV2 } from './theme/createThemeV2';
import { alignFontStacks, supportsMetricOverrides, textNudgeFor } from './theme/fontAlias';
import { cssThemeTokenReader, readSettings, resolveTokens, toCssVars, type V2Tokens } from './theme/tokens';
import { UxVersionProvider } from './UxVersionProvider';

/** Classe posée sur #mindbody-app : deuxième moitié du préfixe de ux-v2.css. */
export const V2_ROOT_CLASS = 'mmv2';

export function mountV2(root: Root, element: HTMLElement, queryClient: QueryClient): void {
  // Squelette du planning rendu par le serveur (UxV2Skeleton.php), encore affiché : React l'efface à son premier rendu
  // et le remplace par le sien, au même reflet (lib/planning/skeleton.ts). Relevé avant tout rendu.
  captureServerSkeleton(element);

  // Le shortcode pose déjà data-ux="v2" quand la v2 est active ; on le garantit si l'app est
  // montée autrement (même source de vérité côté serveur : la config n'est injectée que si active).
  element.setAttribute('data-ux', 'v2');
  element.classList.add(V2_ROOT_CLASS);

  const tokens = resolveTokens(readSettings(), cssThemeTokenReader());
  /*
   * Alias de police déjà déclaré par le serveur (script en ligne du squelette, police connue) : repris dès le premier
   * rendu, comme les libellés du squelette qu'il a peints (sinon le texte remontait de 1 à 2 px au montage, puis
   * redescendait à la pose de l'alias). alignFontStacks le retrouve ensuite tel quel (mêmes métriques).
   */
  const fromServer = serverFontAliases();
  const initial: V2Tokens = {
    ...tokens,
    font: withServerFontAlias(tokens.font, fromServer.font),
    fontDisplay: withServerFontAlias(tokens.fontDisplay, fromServer.fontDisplay),
  };
  Object.entries(toCssVars(initial)).forEach(([name, value]) => element.style.setProperty(name, value));

  Sentry.setTag('ux', 'v2');

  // Texte centré optiquement (theme/fontAlias.ts) : dès que la police du site est chargée et mesurée, ses piles
  // passent sur un alias aux métriques corrigées ; variables CSS et thème Chakra (portails : modales, toasts) suivent.
  // Même arbre React : rien n'est remonté, seul le thème change. Lancé après le premier rendu validé, pour voir aussi
  // les @font-face que <Fonts /> (emotion) insère à ce moment-là ; une seule fois (StrictMode rejoue les effets).
  let aligning = false;
  const alignFonts = () => {
    if (aligning) return;
    aligning = true;
    // Safari ignore ascent-override (l'alias n'y change rien) : les contrôles d'une ligne descendent leur texte de
    // l'écart mesuré (styles/ux-v2.css, « Repli sans ascent-override »).
    if (!supportsMetricOverrides()) {
      void textNudgeFor(tokens.font).then((nudge) => {
        if (nudge === null) return;
        element.style.setProperty('--mmv2-text-nudge', `${nudge}em`);
        element.setAttribute('data-mmv2-nudge', '');
      });
    }
    void alignFontStacks({ font: tokens.font, fontDisplay: tokens.fontDisplay }).then((stacks) => {
      if (!stacks || (stacks.font === initial.font && stacks.fontDisplay === initial.fontDisplay)) return;
      const next: V2Tokens = { ...tokens, ...stacks };
      element.style.setProperty('--mmv2-font', next.font);
      element.style.setProperty('--mmv2-font-display', next.fontDisplay);
      render(next);
    });
  };

  const render = (current: V2Tokens) =>
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          {/* Tous les toasts rendus sous la v2, écrans v1 réutilisés compris : toast de la maquette (lib/toast.tsx). */}
          <ChakraProvider theme={createThemeV2(current)} toastOptions={{ defaultOptions: V2_TOAST_DEFAULTS }}>
            <Fonts />
            <AfterFirstCommit run={alignFonts} />
            <UserProvider>
              <UxVersionProvider version="v2">
                <HashRouter>
                  <AppV2 />
                </HashRouter>
              </UxVersionProvider>
            </UserProvider>
          </ChakraProvider>
        </QueryClientProvider>
      </React.StrictMode>,
    );

  // Connexion venue du site d'une autre marque (docs/ux-v2/HANDOFF.md) : l'app démarre une fois la session ouverte
  // (ou le handoff abandonné), pour que UserProvider lise la fiche du client. Rien en attente : rendu immédiat.
  const handoff = runPendingHandoff();
  if (!handoff) {
    render(initial);
    return;
  }
  root.render(
    <p className="mmv2-handoff" role="status" aria-live="polite">
      Connexion à votre compte…
    </p>,
  );
  void handoff.then(() => render(initial));
}
